import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowRight,
  FiArrowUpRight,
  FiCheck,
  FiHeart,
  FiFeather,
  FiPackage,
  FiShoppingBag,
  FiSun,
  FiTruck,
} from 'react-icons/fi';
import FeaturedCollections from '../components/homepage/FeaturedCollections';
import ServicesSection from '../components/homepage/ServicesSection';
import TestimonialsSection from '../components/homepage/TestimonialsSection';

const farmImages = {
  hero: 'https://images.unsplash.com/photo-1509099381441-ea3c0cf98b94?auto=format&fit=crop&fm=jpg&q=88&w=2200',
  story: 'https://images.unsplash.com/photo-1509100163828-4827534b20da?auto=format&fit=crop&fm=jpg&q=88&w=1800',
  field: 'https://images.unsplash.com/photo-1693777450729-9be8afe098f3?auto=format&fit=crop&fm=jpg&q=88&w=1800',
  community: 'https://images.unsplash.com/photo-1509099408473-99215ef3535b?auto=format&fit=crop&fm=jpg&q=88&w=1800',
};

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.18 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const promises = [
  {
    icon: FiFeather,
    number: '01',
    title: 'Grown with intention',
    text: 'We treat farming as a craft — thoughtful growing, careful handling and attention at every stage.',
  },
  {
    icon: FiSun,
    number: '02',
    title: 'Freshness you can see',
    text: 'From the field to the store, Madisa Farm is built around keeping produce close to the source.',
  },
  {
    icon: FiHeart,
    number: '03',
    title: 'Made for everyday life',
    text: 'Good food should feel accessible, dependable and easy to bring into homes and businesses.',
  },
];

const journey = [
  {
    icon: FiFeather,
    step: '01',
    title: 'Grow',
    text: 'Crops begin with focused cultivation and daily attention on the farm.',
  },
  {
    icon: FiSun,
    step: '02',
    title: 'Harvest',
    text: 'Produce is selected at the right stage and handled with care from the field.',
  },
  {
    icon: FiPackage,
    step: '03',
    title: 'Prepare',
    text: 'Orders are organized for customers, households and business buyers.',
  },
  {
    icon: FiTruck,
    step: '04',
    title: 'Deliver',
    text: 'The store connects customers directly to what Madisa Farm has available.',
  },
];

function Eyebrow({ children, light = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] sm:text-xs font-montserrat font-semibold uppercase tracking-[0.22em] ${light ? 'text-[#dce8b2]' : 'text-[#557338]'}`}>
      <span className={`h-px w-8 ${light ? 'bg-[#dce8b2]/70' : 'bg-[#7e9b55]'}`} />
      {children}
    </div>
  );
}

function Home() {
  return (
    <div className="madisa-farm overflow-hidden bg-[#f7f4ea] text-[#173d2d]">
      {/* HERO */}
      <section className="relative min-h-[calc(100vh-4rem)] lg:min-h-[720px] bg-[#f7f4ea] overflow-hidden">
        <div className="absolute inset-0 farm-grid opacity-[0.24]" />
        <div className="absolute -left-24 top-28 h-72 w-72 rounded-full bg-[#dbe6a8]/[0.35] blur-3xl" />
        <div className="absolute right-[-10%] bottom-[-18%] h-[34rem] w-[34rem] rounded-full bg-[#bccc83]/25 blur-3xl" />

        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-14 pb-12 lg:pb-16">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-center">
            <motion.div {...reveal} className="relative z-10 pt-2 lg:pt-0">
              <div className="inline-flex items-center gap-3 rounded-full border border-[#234d3a]/[0.15] bg-white/70 backdrop-blur px-4 py-2 mb-7 shadow-[0_10px_35px_rgba(23,61,45,0.06)]">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#6f8f43] opacity-50" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#557338]" />
                </span>
                <span className="text-[11px] sm:text-xs font-montserrat font-semibold tracking-[0.16em] uppercase text-[#31533f]">
                  Madisa Farm · From soil to table
                </span>
              </div>

              <h1 className="max-w-4xl font-playfair-display text-[3.4rem] sm:text-6xl lg:text-[5.6rem] xl:text-[6.5rem] leading-[0.88] tracking-[-0.045em] text-[#173d2d]">
                Food begins
                <span className="block italic font-normal text-[#6f8f43] mt-2">with the land.</span>
              </h1>

              <p className="mt-7 sm:mt-8 max-w-xl text-base sm:text-lg lg:text-xl leading-relaxed font-montserrat text-[#496255]">
                Madisa Farm brings carefully grown produce closer to the people who need it — with a simple store, a clear farm story and freshness at the centre of every order.
              </p>

              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/shop"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#173d2d] px-7 py-4 text-sm sm:text-base font-montserrat font-semibold text-white shadow-[0_18px_45px_rgba(23,61,45,0.22)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#214c39]"
                >
                  Shop the harvest
                  <FiArrowUpRight className="text-lg transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <a
                  href="#our-farm"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-[#173d2d]/20 bg-white/70 px-7 py-4 text-sm sm:text-base font-montserrat font-semibold text-[#173d2d] backdrop-blur transition-all duration-300 hover:bg-white hover:-translate-y-1"
                >
                  Discover Madisa Farm
                  <FiArrowRight />
                </a>
              </div>

              <div className="mt-10 sm:mt-12 grid grid-cols-3 max-w-xl border-t border-[#173d2d]/[0.15] pt-6">
                {['Farm-grown', 'Freshly handled', 'Store-ready'].map((label, index) => (
                  <div key={label} className={`${index !== 0 ? 'border-l border-[#173d2d]/[0.12] pl-4 sm:pl-6' : ''}`}>
                    <div className="mb-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#dce8b2]/[0.65] text-[#31533f]">
                      <FiCheck className="text-sm" />
                    </div>
                    <p className="text-xs sm:text-sm font-montserrat font-semibold text-[#31533f]">{label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.12 }}
              className="relative lg:min-h-[610px]"
            >
              <div className="relative h-[500px] sm:h-[590px] lg:h-[650px] rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden shadow-[0_35px_90px_rgba(23,61,45,0.20)] bg-[#244a37]">
                <img
                  src={farmImages.hero}
                  alt="Farming in Sierra Leone"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d291e]/80 via-[#173d2d]/10 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#173d2d]/[0.22] via-transparent to-transparent" />

                <div className="absolute left-5 right-5 sm:left-7 sm:right-auto bottom-5 sm:bottom-7 sm:w-[330px] rounded-[1.6rem] border border-white/20 bg-[#102d22]/[0.78] p-5 sm:p-6 text-white backdrop-blur-xl">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-montserrat font-semibold text-[#dce8b2]">The Madisa standard</p>
                      <p className="mt-2 font-playfair-display text-2xl leading-tight">Closer to where food begins.</p>
                    </div>
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#dce8b2] text-[#173d2d]">
                      <FiFeather className="text-xl" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -left-4 sm:-left-8 top-10 sm:top-16 rounded-2xl border border-white/70 bg-white/[0.88] px-4 py-4 sm:px-5 shadow-[0_18px_45px_rgba(23,61,45,0.14)] backdrop-blur-lg">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#718175] font-montserrat font-semibold">Built around</p>
                <p className="mt-1 text-sm sm:text-base font-montserrat font-semibold text-[#173d2d]">Care · Freshness · Trust</p>
              </div>

              <div className="absolute -right-3 sm:-right-5 top-[38%] rounded-full border border-[#dce8b2]/50 bg-[#173d2d] px-4 py-3 text-white shadow-xl">
                <div className="flex items-center gap-2 text-xs font-montserrat font-semibold">
                  <FiShoppingBag className="text-[#dce8b2]" />
                  Farm store open
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRAND STRIP */}
      <section className="bg-[#173d2d] text-white border-y border-white/10">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-5 overflow-hidden">
          <div className="flex min-w-max items-center gap-8 sm:gap-12 text-[11px] sm:text-xs font-montserrat font-semibold uppercase tracking-[0.18em] text-[#dce8b2]">
            {['Grow with care', 'Harvest with purpose', 'Sell with clarity', 'Serve with freshness', 'Madisa Farm'].map((item, index) => (
              <React.Fragment key={item}>
                <span>{item}</span>
                {index < 4 && <span className="h-1.5 w-1.5 rounded-full bg-[#8eaa5c]" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section id="our-farm" className="relative bg-[#fffdf8] py-20 sm:py-24 lg:py-32">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...reveal} className="grid lg:grid-cols-[0.82fr_1.18fr] gap-10 lg:gap-20 items-end mb-14 lg:mb-18">
            <div>
              <Eyebrow>What Madisa Farm stands for</Eyebrow>
              <h2 className="mt-5 max-w-xl font-playfair-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] text-[#173d2d]">
                Farming that feels <span className="italic font-normal text-[#6f8f43]">close, clear and real.</span>
              </h2>
            </div>
            <p className="max-w-xl lg:justify-self-end text-base sm:text-lg leading-relaxed font-montserrat text-[#5d6f63]">
              The farm is more than where produce is grown. It is the beginning of quality, availability and trust. Madisa Farm brings those pieces together and makes the journey into the store easy to understand.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
            {promises.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: index * 0.08 }}
                  className="group relative min-h-[320px] overflow-hidden rounded-[2rem] border border-[#173d2d]/10 bg-[#f7f4ea] p-7 sm:p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(23,61,45,0.12)]"
                >
                  <div className="absolute right-5 top-4 font-playfair-display text-6xl text-[#173d2d]/[0.055]">{item.number}</div>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#173d2d] text-[#dce8b2] shadow-lg transition-transform duration-500 group-hover:rotate-3 group-hover:scale-105">
                    <Icon className="text-2xl" />
                  </div>
                  <h3 className="mt-16 sm:mt-20 font-playfair-display text-2xl sm:text-3xl text-[#173d2d]">{item.title}</h3>
                  <p className="mt-4 text-sm sm:text-base leading-relaxed font-montserrat text-[#607167]">{item.text}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-[#eaf0d6] py-20 sm:py-24 lg:py-32">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-stretch">
            <motion.div {...reveal} className="relative min-h-[520px] sm:min-h-[620px] overflow-hidden rounded-[2rem] sm:rounded-[2.6rem] bg-[#173d2d]">
              <img src={farmImages.story} alt="Harvesting crops" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#102b20]/75 via-transparent to-transparent" />
              <div className="absolute left-5 right-5 bottom-5 sm:left-7 sm:right-7 sm:bottom-7 flex items-end justify-between gap-6 rounded-2xl border border-white/20 bg-white/10 p-5 text-white backdrop-blur-md">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-montserrat font-semibold text-[#dce8b2]">At the source</p>
                  <p className="mt-2 font-playfair-display text-2xl sm:text-3xl">The farm comes first.</p>
                </div>
                <FiFeather className="hidden sm:block text-4xl text-[#dce8b2]" />
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="flex flex-col justify-center py-4 lg:py-10">
              <Eyebrow>Our farm story</Eyebrow>
              <h2 className="mt-5 font-playfair-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.035em] text-[#173d2d]">
                Keep the distance between <span className="italic font-normal text-[#6f8f43]">farm and customer</span> simple.
              </h2>
              <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed font-montserrat text-[#4f6758]">
                Madisa Farm is built around a straightforward idea: grow with care, make what is available easy to discover, and give customers a direct path to buy from the farm through the existing store.
              </p>

              <div className="mt-9 space-y-5 border-t border-[#173d2d]/[0.15] pt-8">
                {[
                  'A farm-first identity that makes the source visible',
                  'An existing online store that remains simple to shop',
                  'A clearer connection between what is grown and what is sold',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-4">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#173d2d] text-[#dce8b2]">
                      <FiCheck className="text-sm" />
                    </div>
                    <p className="text-sm sm:text-base font-montserrat font-medium text-[#31533f]">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/about" className="group inline-flex items-center gap-3 rounded-full border border-[#173d2d]/20 bg-white/[0.65] px-6 py-3.5 text-sm font-montserrat font-semibold text-[#173d2d] hover:bg-white transition">
                  Read our story <FiArrowRight className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-3 rounded-full px-6 py-3.5 text-sm font-montserrat font-semibold text-[#31533f] hover:text-[#173d2d] transition">
                  Talk to the farm <FiArrowUpRight />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STORE / EXISTING PRODUCT ENGINE */}
      <section className="bg-[#fffdf8] py-20 sm:py-24 lg:py-28">
        <motion.div {...reveal}>
          <FeaturedCollections
            eyebrow="From the Madisa store"
            title="Fresh picks from the farm"
            description="Browse what is currently available and order through the same store experience already built into the site."
          />
        </motion.div>
      </section>

      {/* HARVEST JOURNEY */}
      <section className="relative overflow-hidden bg-[#122f24] py-20 sm:py-24 lg:py-32 text-white">
        <div className="absolute inset-0 farm-grid-dark opacity-[0.22]" />
        <div className="absolute right-[-12%] top-[-30%] h-[34rem] w-[34rem] rounded-full bg-[#6f8f43]/20 blur-[100px]" />

        <div className="relative max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...reveal} className="max-w-3xl">
            <Eyebrow light>From field to order</Eyebrow>
            <h2 className="mt-5 font-playfair-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02]">
              A simple journey from <span className="italic font-normal text-[#dce8b2]">growing to buying.</span>
            </h2>
          </motion.div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-[2rem] border border-white/10 bg-white/10">
            {journey.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: index * 0.07 }}
                  className="group relative min-h-[300px] bg-[#15382a]/95 p-7 sm:p-8 transition-colors duration-500 hover:bg-[#1b4635]"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-montserrat font-semibold uppercase tracking-[0.18em] text-[#a9bc7b]">Step {item.step}</span>
                    <Icon className="text-2xl text-[#dce8b2]" />
                  </div>
                  <div className="absolute left-7 right-7 bottom-8 sm:left-8 sm:right-8">
                    <h3 className="font-playfair-display text-3xl text-white">{item.title}</h3>
                    <p className="mt-4 text-sm sm:text-base leading-relaxed font-montserrat text-white/[0.65]">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES - SAME BACKEND DATA */}
      <div className="farm-services-wrap bg-[#f7f4ea]">
        <ServicesSection />
      </div>

      {/* VISUAL FARM MOMENT */}
      <section className="bg-[#fffdf8] py-20 sm:py-24 lg:py-32">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-5 lg:gap-6">
            <motion.div {...reveal} className="relative min-h-[520px] overflow-hidden rounded-[2.2rem] bg-[#173d2d]">
              <img src={farmImages.field} alt="Farm field" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f2c20]/[0.85] via-[#173d2d]/10 to-transparent" />
              <div className="absolute left-6 right-6 bottom-6 sm:left-9 sm:right-9 sm:bottom-9 text-white">
                <Eyebrow light>Rooted in the land</Eyebrow>
                <h2 className="mt-4 max-w-2xl font-playfair-display text-4xl sm:text-5xl leading-[1.04]">The farm should be something customers can feel.</h2>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.08 }} className="grid gap-5 lg:gap-6">
              <div className="relative min-h-[300px] overflow-hidden rounded-[2.2rem] bg-[#dce8b2] p-7 sm:p-8">
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full border-[28px] border-[#173d2d]/5" />
                <div className="relative flex h-full flex-col justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#173d2d] text-[#dce8b2]">
                    <FiShoppingBag className="text-xl" />
                  </div>
                  <div className="mt-16">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-montserrat font-semibold text-[#557338]">Already connected</p>
                    <h3 className="mt-3 font-playfair-display text-3xl sm:text-4xl text-[#173d2d]">The store stays exactly where customers need it.</h3>
                    <Link to="/shop" className="group mt-6 inline-flex items-center gap-3 text-sm font-montserrat font-semibold text-[#173d2d]">
                      Browse the store <FiArrowRight className="transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="relative min-h-[280px] overflow-hidden rounded-[2.2rem] bg-[#173d2d]">
                <img src={farmImages.community} alt="Farm work" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-[#102d22]/[0.45]" />
                <div className="absolute inset-x-6 bottom-6 rounded-2xl border border-white/20 bg-black/[0.15] p-5 text-white backdrop-blur-md">
                  <p className="font-playfair-display text-2xl">People, land and the work between them.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - EXISTING DATA */}
      <div className="farm-testimonials-wrap">
        <TestimonialsSection />
      </div>

      {/* FINAL CTA */}
      <section className="bg-[#f7f4ea] px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24 lg:pb-28">
        <motion.div
          {...reveal}
          className="relative max-w-[1320px] mx-auto overflow-hidden rounded-[2rem] sm:rounded-[2.8rem] bg-[#d8a85b] px-6 py-14 sm:px-10 sm:py-16 lg:px-16 lg:py-20"
        >
          <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full border-[52px] border-white/10" />
          <div className="absolute -left-20 -bottom-28 h-80 w-80 rounded-full bg-[#173d2d]/10" />
          <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-end">
            <div>
              <p className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-montserrat font-semibold text-[#173d2d]/70">Madisa Farm store</p>
              <h2 className="mt-4 max-w-3xl font-playfair-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.035em] text-[#173d2d]">
                See what the farm has for you today.
              </h2>
            </div>
            <Link
              to="/shop"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-[#173d2d] px-8 py-4 text-sm sm:text-base font-montserrat font-semibold text-white shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-[#214c39]"
            >
              Visit the store
              <FiArrowUpRight className="text-lg transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

export default Home;
